import { useEffect, useState } from "react";

/**
 * Whether the viewport matches a media query, live.
 *
 * The viewport, not an element — `use-narrow.ts` is the one to reach for when
 * the question is about a box inside the page, which is most of them: a table
 * loses three hundred pixels when the sidebar opens and the window never
 * changes. This is for the handful of things that genuinely answer to the
 * screen: an overlay that covers it, the sidebar that measures it, and a grid
 * whose shape is decided by how much glass there is.
 *
 * Starts from the answer rather than from `false`, so nothing renders in the
 * wrong shape for a frame and then jumps. `matchMedia` is missing in jsdom
 * unless the test setup stubs it, hence the optional call.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia?.(query).matches === true);
  useEffect(() => {
    const list = window.matchMedia?.(query);
    if (!list) return;
    const measure = () => setMatches(list.matches);
    measure();
    list.addEventListener("change", measure);
    return () => list.removeEventListener("change", measure);
  }, [query]);
  return matches;
}

/**
 * Below Tailwind's `sm`: one hand, one column, and no room for a table.
 *
 * Spelled once because three screens ask it, and a breakpoint written out
 * three times is a breakpoint that will be changed in two places.
 */
export const PHONE = "(max-width: 39.99rem)";
